import { isDefined, isNonEmpty } from '@sgme/fp';
import type { ReloadFormRequest } from 'epic/common-form/ReloadFormRequest';
import { IdType } from 'epic/common-form/ReloadFormRequest';
import { isUndefined } from 'lodash-es';
import type { FormError } from 'models/formError';
import type { EnrichFormRequest } from 'models/forms/EnrichFormRequest';
import type { FieldMetadata } from 'models/forms/fields/FieldMetadata';
import type { BlockMetadata } from 'models/metadata/block.definition';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { ProcessingChain } from 'models/ProcessingChain';
import { ProductVariation, ProductVariationBase } from 'models/productsReferential';
import type { QuoteRequestPayload } from 'models/quote/QuoteRequestPayload';
import type { QuoteWarning } from 'models/QuoteWarning';
import { createSelector } from 'reselect';
import type { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';
import type { DataById } from 'state/formData/formData.model';
import type { NextTradeAuthorizationResult } from 'state/quotes/trades/trades.model';
import { isFeatureEnabled } from 'state/selectors';
import type { AppState } from 'state/state.model';
import { isUserPreferencesPrivateMode } from 'state/userPreferences/userPreferences.selectors';
import { getOrdersBasketFieldIdsByProductVariation } from 'state/workspaces/ordersBasket/productDefinitions/productDefinitions.selectors';
import {
  getNumberOfOrdersBasketFormsByProductVariation,
  getOrdersBasketFormIdsByProductVariation,
  getOrdersBasketFormIdsByTabId,
} from 'state/workspaces/ordersBasket/tabContent/tabContent.selectors';

import type { ExposedFormStatus, FormStatus } from '../forms/models';
import {
  isOrdersBasketFormBookableByStatus,
  isOrdersBasketFormCancelableByStatus,
  isOrdersBasketFormDeletableByStatus,
  isOrdersBasketFormReadyToBook,
  isOrdersBasketFormSelectableByStatus,
  translateOrdersBasketFormStatus,
} from './helpers';
import type {
  OrdersBasketForm,
  OrdersBasketFormMetadata,
  OrdersBasketFormsState,
  OrdersBasketQuoteResponse,
} from './models';

export function selectOrdersBasketFormsState(appState: AppState): OrdersBasketFormsState {
  return appState.ordersBasket;
}

export function getOrdersBasketTotalNumberOfForms(appState: AppState): number {
  const forms = selectOrdersBasketFormsState(appState);
  return Object.keys(forms).length;
}

export function getOrdersBasketFormById(appState: AppState, formId: string): OrdersBasketForm | undefined {
  const state = selectOrdersBasketFormsState(appState);
  return state[formId];
}

export function getOrdersBasketFormsByProductVariation(
  appState: AppState,
  productVariation: ProductVariationBase,
): OrdersBasketForm[] {
  const state = selectOrdersBasketFormsState(appState);
  return Object.values(state).filter(
    form =>
      form.productSubType === productVariation.productSubType && form.productType === productVariation.productType,
  );
}

export function getTradeAuthorizationResultById(
  appState: AppState,
  formId: string,
): NextTradeAuthorizationResult | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  return form ? form.tradeAuthorization : undefined;
}

export function getOrdersBasketExposedFormStatusById(
  appState: AppState,
  formId: string,
): ExposedFormStatus | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  return form ? translateOrdersBasketFormStatus(form.status) : undefined;
}

export function getOrdersBasketFormStatusById(appState: AppState, formId: string): FormStatus | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  return form ? form.status : undefined;
}

export function isOrdersBasketFormSelected(appState: AppState, formId: string): boolean {
  const form = getOrdersBasketFormById(appState, formId);
  return form ? form.isSelected : false;
}

export function getOrdersBasketSelectableFormIdByProductVariation(
  appState: AppState,
  productDescription: ProductVariationBase,
): string[] {
  const formIds = getOrdersBasketFormIdsByProductVariation(appState, productDescription);
  return formIds.filter(formId => isOrdersBasketFormSelectable(appState, formId));
}

export function isEveryOrdersBasketSelectableFormSelectedByProductVariation(
  appState: AppState,
  productVariation: ProductVariationBase,
): boolean {
  const formIds = getOrdersBasketSelectableFormIdByProductVariation(appState, productVariation);
  return formIds.length > 0 ? formIds.every(formId => isOrdersBasketFormSelected(appState, formId)) : false;
}

export function isNoOrdersBasketSelectableFormSelectedByProductVariation(
  appState: AppState,
  productVariation: ProductVariationBase,
): boolean {
  const formIds = getOrdersBasketSelectableFormIdByProductVariation(appState, productVariation);
  return formIds.length > 0 ? formIds.every(formId => !isOrdersBasketFormSelected(appState, formId)) : true;
}

export function getOrdersBasketSelectedFormsIdByProductVariation(
  appState: AppState,
  productVariation: ProductVariationBase,
): string[] {
  const forms = getOrdersBasketFormsByProductVariation(appState, productVariation);
  return forms.filter(form => form.isSelected).map(form => form.negotiationId);
}

export function getOrdersBasketNumberOfSelectedFormsByProductSubType(
  appState: AppState,
  productVariation: ProductVariationBase,
): number {
  return getOrdersBasketSelectedFormsIdByProductVariation(appState, productVariation).length;
}

export function isOrdersBasketFormSelectable(appState: AppState, formId: string): boolean {
  const form = getOrdersBasketFormById(appState, formId);
  return form ? isOrdersBasketFormSelectableByStatus(form.status) : false;
}

export function getOrdersBasketBlockMetadata(
  appState: AppState,
  formId: string,
  blockId: string,
): BlockMetadata | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  const metadata = form?.result?.blocksMetadata;
  return metadata && Object.keys(metadata).includes(blockId) ? metadata[blockId] : undefined;
}

export function getOrdersBasketFieldMetadata(
  appState: AppState,
  formId: string,
  fieldId: string,
): FieldMetadata | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  const metadata = form?.result?.fieldsMetadata;
  return metadata && Object.keys(metadata).includes(fieldId) ? metadata[fieldId] : undefined;
}

export function getOrdersBasketFieldsMetadata(appState: AppState, formId: string): FieldMetadataById {
  const form = getOrdersBasketFormById(appState, formId);
  return form?.result?.fieldsMetadata ?? {};
}

export function getOrdersBasketValidationErrors(appState: AppState, formId: string, nbOfErrors?: number): FormError[] {
  const form = getOrdersBasketFormById(appState, formId);
  return form?.result?.errors?.slice(0, nbOfErrors) ?? [];
}

export function isOrdersBasketColumnVisible(
  appState: AppState,
  productDescription: ProductVariationBase,
  fieldId: string,
): boolean {
  const formIds = getOrdersBasketFormIdsByProductVariation(appState, productDescription);
  if (formIds.length === 0) {
    return true;
  }
  const metadata = formIds.map(formId => getOrdersBasketFieldMetadata(appState, formId, fieldId)).filter(isDefined);
  return metadata.length === 0 || metadata.some(i => !i.isHidden);
}

export function getOrdersBasketFieldValue(appState: AppState, formId: string, fieldId: string): unknown {
  const form = getOrdersBasketFormById(appState, formId);
  const dirtyValue = form?.dirtyParameters?.[fieldId];
  const resultValue = form?.result?.values[fieldId];
  return dirtyValue !== undefined ? dirtyValue : resultValue;
}

export function getOrdersBasketDataByFormId(appState: AppState, formId: string) {
  const form = getOrdersBasketFormById(appState, formId);
  const dirtyValue = form?.dirtyParameters;
  const resultValue = form?.result?.values;
  const mergedValue = { ...resultValue };
  for (const property in dirtyValue) {
    if (dirtyValue.hasOwnProperty(property) && mergedValue.hasOwnProperty(property)) {
      mergedValue[property] = dirtyValue[property];
    }
  }
  return mergedValue;
}

export function getOrdersBasketProductVariation(appState: AppState, formId: string): ProductVariation {
  const form = getOrdersBasketFormMetadata(appState, formId);
  return {
    productType: form?.productType ?? '',
    productSubType: form?.productSubType ?? '',
    wrapper: form?.wrapper ?? '',
    requestType: 'RequestForQuote',
  };
}

export function getAllOrdersBasketFormIdsByProductSubType(appState: AppState, product: ProductVariationBase) {
  const allOrdersBasketFormIdsByProductSubType: string[] = [];
  const orderBasketForms = appState.ordersBasket;
  for (const formId in orderBasketForms) {
    const form = orderBasketForms[formId];
    if (form.productSubType === product.productSubType && form.productType === product.productType) {
      allOrdersBasketFormIdsByProductSubType.push(formId);
    }
  }
  return allOrdersBasketFormIdsByProductSubType;
}

export function getOrdersBasketNumberOfVisibleFieldsByProductDescription(
  appState: AppState,
  productDescription: ProductVariationBase,
): number {
  const fieldIds = getOrdersBasketFieldIdsByProductVariation(appState, productDescription);
  const nbOfForms = getNumberOfOrdersBasketFormsByProductVariation(appState, productDescription);
  return nbOfForms > 0
    ? fieldIds.filter(fieldId => isOrdersBasketColumnVisible(appState, productDescription, fieldId)).length
    : fieldIds.length;
}

export function getOrdersBasketFormDirtyParameters(appState: AppState, formId: string): DataById | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  return form?.dirtyParameters;
}

export function getOrdersBasketFormUpdatingParameters(appState: AppState, formId: string): DataById | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  return form?.updatingParameters;
}

export function getOrdersBasketEnrichFormRequest(appState: AppState, formId: string): EnrichFormRequest | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  if (
    form &&
    form.dirtyParameters &&
    form.result &&
    form.version !== undefined &&
    form.productType &&
    form.productSubType &&
    form.wrapper
  ) {
    return {
      negotiationId: form.negotiationId,
      version: form.version,
      productType: form.productType,
      productSubType: form.productSubType,
      wrapper: form.wrapper,
      variationParameters: form.dirtyParameters,
    };
  }
  return undefined;
}

export function canOrdersBasketSelectedFormsBeBooked(
  appState: AppState,
  productVariation: ProductVariationBase,
): boolean {
  const formIds = getOrdersBasketSelectedFormsIdByProductVariation(appState, productVariation);
  return formIds.length > 0
    ? formIds.every(formId => {
        const form = getOrdersBasketFormById(appState, formId);
        if (!form || !form.result) {
          return false;
        }

        const isBookable = isOrdersBasketFormBookableByStatus(form.status);
        const isReadyToBook = isOrdersBasketFormReadyToBook(form.result);
        return isBookable && isReadyToBook;
      })
    : false;
}

export function canOrdersBasketSelectedFormsBeCanceled(
  appState: AppState,
  productVariation: ProductVariationBase,
): boolean {
  const formIds = getOrdersBasketSelectedFormsIdByProductVariation(appState, productVariation);
  return formIds.length > 0
    ? formIds.every(formId => {
        const form = getOrdersBasketFormById(appState, formId);
        if (!form) {
          return false;
        }

        const isCancelable = isOrdersBasketFormCancelableByStatus(form.status);
        return isCancelable;
      })
    : false;
}

export function canOrdersBasketSelectedFormsBeDeleted(
  appState: AppState,
  productVariation: ProductVariationBase,
): boolean {
  const formIds = getOrdersBasketSelectedFormsIdByProductVariation(appState, productVariation);
  return formIds.length > 0
    ? formIds.every(formId => {
        const form = getOrdersBasketFormById(appState, formId);
        if (!form) {
          return false;
        }

        const isDeletable = isOrdersBasketFormDeletableByStatus(form.status);
        return isDeletable;
      })
    : false;
}

export function getOrdersBasketQuoteRequestPayload(
  appState: AppState,
  formId: string,
): QuoteRequestPayload | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  return form?.result
    ? {
        negotiationId: form.negotiationId,
        version: form.version,
        productType: form.productType,
        productSubType: form.productSubType,
        quoteQuery: form.result.quoteQuery,
      }
    : undefined;
}

export function getOrdersBasketFormIdByQuoteId(appState: AppState, quoteId: string): string | undefined {
  const state = selectOrdersBasketFormsState(appState);
  const matchedForm = Object.values(state).find(form => form?.quoteAcknowledgement?.quoteId === quoteId);
  return matchedForm?.negotiationId;
}

export function getOrdersBasketQuoteIdByFormId(appState: AppState, formId: string): string | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  return form?.quoteAcknowledgement?.quoteId;
}

export function getQuoteResponseById(appState: AppState, formId: string): OrdersBasketQuoteResponse | undefined {
  const state = selectOrdersBasketFormsState(appState);
  const quoteResponse = state[formId]?.quoteResponse;
  return quoteResponse;
}

export function getOrdersBasketFormErrorsById(
  appState: AppState,
  formId: string,
): readonly NextWorkflowError[] | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  if (!form) {
    return undefined;
  }
  const quoteResponseErrors = form.quoteResponse?.rejections;
  const tradeAuthErrors = form.tradeAuthorization?.rejections;
  const tradeResponseErrors = form.tradeResponse?.rejections;
  const errors = tradeResponseErrors ?? tradeAuthErrors ?? quoteResponseErrors;

  return errors;
}

export function getOrdersBasketFormJsonRepresentation(appState: AppState, formId: string): string | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  return form !== undefined ? JSON.stringify(form, undefined, 2) : undefined;
}

export function getOrdersBasketFormQuoteWarnings(appState: AppState, formId: string): QuoteWarning[] | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  return form ? form.quoteResponse?.warnings : undefined;
}

export function isOrdersBasketFormWithWarning(appState: AppState, formId: string): boolean {
  const warnings = getOrdersBasketFormQuoteWarnings(appState, formId);
  return isNonEmpty(warnings);
}

export function getOrdersBasketFormMetadata(appState: AppState, formId: string): OrdersBasketFormMetadata | undefined {
  const form = getOrdersBasketFormById(appState, formId);
  return form
    ? {
        negotiationId: form.negotiationId,
        status: form.status,
        wrapper: form.wrapper,
        productType: form.productType,
        productSubType: form.productSubType,
        quoteAcknowledgement: form.quoteAcknowledgement,
        quoteResponse: form.quoteResponse,
        tradeResponse: form.tradeResponse,
      }
    : undefined;
}

export function getOrdersBasketWorkflowErrors(appState: AppState, tabId: string): NextWorkflowError[] {
  const formIds = getOrdersBasketFormIdsByTabId(appState, tabId);
  return formIds.reduce<NextWorkflowError[]>((acc, formId) => {
    const form = getOrdersBasketFormById(appState, formId);
    if (!form) {
      return acc;
    }
    const quoteRejections = form.quoteResponse?.rejections ?? [];
    const tradeRejections = form.tradeResponse?.rejections ?? [];
    return [...acc, ...quoteRejections, ...tradeRejections];
  }, []);
}

export function getOrdersBasketIsEditable(appState: AppState, formId: string): boolean {
  const notEditableStatus: FormStatus[] = [
    'QUOTE_REQUESTED',
    'QUOTED_PENDING_AUTHORIZATION',
    'AUTHORIZED_PENDING_QUOTE',
    'EXECUTING',
    'EXECUTED',
    'EXECUTED_WITH_WARNING',
    'BOOKING_REQUESTED',
    'BOOKING_ACKED',
  ];
  const status = getOrdersBasketFormById(appState, formId)?.status;
  if (isUndefined(status)) {
    return true;
  }
  return !notEditableStatus.includes(status);
}

export const getOrdersBasketNumberOfFormsByProductSubType = createSelector(
  [selectOrdersBasketFormsState],
  (forms: Record<string, OrdersBasketForm>): Record<string, number> => {
    const result: Record<string, number> = {};
    Object.values(forms).forEach(form => {
      const subType = form.productSubType;
      if (subType) {
        result[subType] = (result[subType] || 0) + 1;
      }
    });
    return result;
  },
);

export function isOrdersBasketFormBookWithExecutionEnabled(appState: AppState, formId: string): boolean {
  const form = getOrdersBasketFormById(appState, formId);
  if (!form || !form.result || !Array.isArray(form.result.actions)) {
    return false;
  }
  return form.result.actions.some(action => action.name === 'BookWithExecution' && action.isDisabled === undefined);
}

export function getOrdersBasketInitializeFormRequest(
  state: AppState,
  params: {
    id: string;
    idType: IdType;
    tradeDate: string;
    processingChain?: ProcessingChain;
    pricerRfqId?: string;
  },
): ReloadFormRequest {
  const isReloadTessaQuotesInOrdersBasketHKEnabled = isFeatureEnabled(state, 'reloadTessaQuotesInOrdersBasketHK');
  const isPrivateMode = isUserPreferencesPrivateMode(state);
  const baseFormRequest = {
    id: params.id,
    idType: params.idType,
    tradeDate: params.tradeDate,
    isPrivateMode,
    processingChain: params.processingChain,
  };
  const formRequest = isReloadTessaQuotesInOrdersBasketHKEnabled
    ? { ...baseFormRequest, pricerRfqId: params.pricerRfqId }
    : baseFormRequest;
  return formRequest;
}
